import { Suspense, lazy, memo } from 'react'
import { useTranslation } from 'react-i18next'

import { OfferNames } from '@wwt/ui/components/Offer'

import { ReservationButtonByOfferStatus } from '@/features/ReservationButtons'
import { Button } from '@/shared/ui/Button.tsx'
import { UnderDevelopment } from '@/shared/ui/UnderDevelopment.tsx'

import { useHotelSection } from '../../model/useHotelSection'
import { TabSection } from '../../types'
import { GroupWithFeatures } from '../GroupWithFeatures'
import * as Layout from './layout'

const MapInfo = lazy(() =>
	import('@wwt/widgets/map').then(mod => ({ default: mod.MapInfo }))
)

export const HotelSection = memo(
	({ offer, onShowOfferConfiguration }: TabSection) => {
		const {
			address,
			averageRate,
			description,
			featureGroups,
			name,
			longitude,
			latitude,
			stars,
			hotelType,
			isConfigurationOfferDisabled
		} = useHotelSection(offer)
		const { t } = useTranslation('room-page')

		return (
			<div className="flex flex-col items-start justify-between gap-[2.8125rem]">
				<div className="grid grid-cols-1 gap-6 lg:grid-cols-3 lg:gap-6 xl:gap-10">
					<div className="col-span-1 lg:col-span-2">
						<div className="flex flex-col items-start gap-6 max-md:px-5">
							<OfferNames
								// TODO: Move errors to i18n
								name={name ?? 'Hotel name not found'}
								stars={stars}
								address={address ?? 'Hotel address not found'}
								averageRate={averageRate ?? 'N/A'}
								hotelType={hotelType}
							/>

							<Layout.Body>{description}</Layout.Body>
						</div>

						<UnderDevelopment asChild>
							<Button
								className="ts-3 text-primary-100 mt-4 max-lg:px-5"
								data-wwt-id="room-offer__hotel-section-show-photos--button"
								variant="link"
							>
								{t('tabs.showPhotos')}
							</Button>
						</UnderDevelopment>
					</div>

					<div className="col-span-1">
						<div className="flex flex-col gap-8 text-right">
							<Suspense fallback={null}>
								<MapInfo
									className="h-[20.5rem] w-full rounded-md"
									longitude={longitude}
									latitude={latitude}
								/>
							</Suspense>

							<ReservationButtonByOfferStatus
								className="w-[18.5rem] self-end lg:w-full"
								offer={offer}
							/>
						</div>
					</div>
				</div>

				<div className="flex flex-col items-start gap-8 max-lg:px-5">
					<GroupWithFeatures
						className="hotel-section-group"
						groupsWithFeatures={featureGroups}
						defaultGroupName={t('defaultGroupName', {
							ns: 'translation'
						})}
						onShowOfferConfiguration={onShowOfferConfiguration}
						featureListClassName="hotel-section-feature-list"
						isDisabledOfferConfiguration={isConfigurationOfferDisabled}
					/>
				</div>
			</div>
		)
	}
)

HotelSection.displayName = 'HotelSection'
